import { AxiosError } from 'axios';
import clsx from 'clsx';

import {
  ChangeEventHandler,
  FC,
  FormEventHandler,
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import PhoneInputWithCountrySelect, { isValidPhoneNumber } from 'react-phone-number-input';
import { Link, useNavigate, useParams } from 'react-router-dom';

import styles from './sign-up-form.module.scss';
import 'react-phone-number-input/style.css';

import { ISignUpFormProps, THandleSignInRequest, TInputsData } from './types';

import {
  Button,
  Checkbox,
  EButtonKinds,
  Input,
  Paragraph,
  PhoneInput,
  ProgressBar,
  SmsInput,
} from '../ui';

import { authLogin, authSetPhone, authSignUp } from '~utils';

const SignUpForm: FC<ISignUpFormProps> = ({ setIsHidden, className = '', ...rest }) => {
  const [phoneNum, setPhoneNum] = useState('');
  const [inputsData, setInputsData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    city: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [loadingError, setLoadingError] = useState('');
  const [isPrivacyChecked, setIsPrivacyChecked] = useState(false);

  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);
  const [codeInputsData, setCodeInputsData] = useState<TInputsData>({
    0: '',
    1: '',
    2: '',
    3: '',
  });

  const navigate = useNavigate();

  const { t } = useTranslation();

  const [codeBtnState, setCodeBtnState] = useState({
    sec: 0,
    text: t('sign-in.sent-sms-btn', { amount: 0 }),
  });
  const [paragraphText, setParagraphText] = useState('');

  const phoneValidate: Record<string, boolean> = {
    1: true,
    2: isValidPhoneNumber(phoneNum || ''),
  };

  const { step } = useParams<{ step: string }>();
  const stepNum = Number(step ?? 1);

  const phoneInputRef = useRef(null);
  const input0Ref = useRef(null);
  const firstNameInputRef = useRef<HTMLInputElement>(null);
  const lastNameInputRef = useRef<HTMLInputElement>(null);
  const companyNameInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const cityInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const startCodeTimer = useCallback(() => {
    let sec = 60;

    setCodeBtnState({
      sec: 60,
      text: t('sign-in.sent-sms-btn', { amount: 60 }),
    });

    const id = setInterval(() => {
      sec--;

      setCodeBtnState({
        sec,
        text: sec ? t('sign-in.sent-sms-btn', { amount: sec }) : t('sign-in.resent-sms-btn'),
      });

      if (!sec) {
        clearInterval(id);
      }
    }, 1000);

    setIntervalId(id);
  }, [t]);

  const handleRequest = () => {
    setLoadingError('');
    setIsLoading(true);

    authSetPhone({ phone_number: phoneNum, email: inputsData.email, operation: 'signup' })
      .then(({ data }) => {
        setParagraphText(data.message);
        setIsHidden(true);
        startCodeTimer();
        handleRemoveError();

        if (stepNum === 2) {
          navigate('/sign-up/3');
        }
      })
      .catch(err => {
        console.log(err);

        if (err.response?.status === 400) {
          setLoadingError(
            err.response.data.errors?.[0]?.detail || t('sign-up.user-already-exists')!,
          );
        } else {
          setLoadingError(t('sign-up.loading-error')!);
        }
      })
      .finally(() => setIsLoading(false));
  };

  const handleAuth: THandleSignInRequest = useCallback(
    otp => {
      if (!inputsData.firstName || !inputsData.lastName) {
        navigate('/sign-up/1');
        return;
      }

      if (!inputsData.email || !phoneNum) {
        navigate('/sign-up/2');
        return;
      }

      setIsLoading(true);
      authSignUp({
        first_name: inputsData.firstName,
        last_name: inputsData.lastName,
        company_name: inputsData.companyName,
        email: inputsData.email,
        city: inputsData.city,
        phone_number: phoneNum,
        otp,
      })
        .then(() => {
          authLogin({ phone_number: phoneNum, otp })
            .then(({ access }) => {
              localStorage.setItem('access', access);

              navigate('/', { replace: true });
            })
            .catch((err: AxiosError) => {
              console.log(err);

              setLoadingError(t('sign-up.loading-error')!);
            });
        })
        .catch(err => {
          console.log(err);
          if (err.response?.status === 401) {
            setLoadingError(t('sign-up.code-error')!);
          } else {
            setLoadingError(t('sign-up.loading-error')!);
          }
        })
        .finally(() => setIsLoading(false));
    },
    [phoneNum, inputsData, navigate, t],
  );

  const handleRemoveError = useCallback(() => {
    setCodeInputsData({
      0: '',
      1: '',
      2: '',
      3: '',
    });
    setLoadingError('');
    (input0Ref as MutableRefObject<HTMLInputElement | null>)?.current?.focus();
  }, []);

  const handleChangePhone = (v: string | undefined) => {
    setPhoneNum(v ?? '');
  };

  const handleChangeInputs: ChangeEventHandler<HTMLInputElement> = useCallback(e => {
    setInputsData(prev => ({
      ...prev,
      [e.target.id]: e.target.value,
    }));
  }, []);

  const toSecondStep = () => {
    navigate('/sign-up/2');

    phoneInputFocus();
  };

  const handleSubmit: FormEventHandler<HTMLFormElement> = e => {
    e.preventDefault();

    if (stepNum === 1) {
      toSecondStep();
    } else if (stepNum === 2) {
      if (inputsData.firstName && inputsData.lastName) {
        handleRequest();
      } else {
        navigate('/sign-up/1');
      }
    } else if (stepNum === 3) {
      handleRequest();
    }
  };

  const phoneInputFocus = () => {
    setTimeout(() => {
      if (phoneInputRef.current) {
        (phoneInputRef.current as HTMLInputElement).focus();
      }
    }, 0);
  };

  const firstNameInputFocus = () => {
    setTimeout(() => {
      if (firstNameInputRef.current) {
        firstNameInputRef.current.focus();
      }
    }, 0);
  };

  const handleFocusToRemoveError = () => {
    if (loadingError) {
      setLoadingError('');
    }
  };

  const handleChangePhoneBtnClick = () => {
    navigate(-1);
  };

  const handleChangePrivacyChecked: ChangeEventHandler<HTMLInputElement> = value => {
    setIsPrivacyChecked(value.target.checked);
  };

  const handleProgressBarFirstStep = () => {
    if (stepNum === 1) return;

    navigate('/sign-up/1');
  };

  const handleProgressBarSecondStep = () => {
    if (stepNum === 2) return;

    if (formRef.current?.checkValidity()) {
      toSecondStep();
    } else {
      formRef.current?.reportValidity();
    }
  };

  const renderStepContent = () => {
    if (stepNum === 1) {
      return (
        <>
          <Input
            placeholder={t('inputs.first-name') || ''}
            fieldClassName={styles.input}
            type='text'
            id='firstName'
            required
            minLength={2}
            maxLength={20}
            value={inputsData.firstName}
            onChange={handleChangeInputs}
            onFocus={handleFocusToRemoveError}
            ref={firstNameInputRef}
            isError={!!loadingError}
            autoComplete='on'
          />
          <Input
            placeholder={t('inputs.last-name') || ''}
            fieldClassName={styles.input}
            type='text'
            id='lastName'
            required
            minLength={2}
            maxLength={20}
            value={inputsData.lastName}
            onChange={handleChangeInputs}
            onFocus={handleFocusToRemoveError}
            isError={!!loadingError}
            ref={lastNameInputRef}
            autoComplete='on'
          />
          <Input
            placeholder={t('inputs.company-name') || ''}
            fieldClassName={styles.input}
            type='text'
            id='companyName'
            minLength={2}
            maxLength={40}
            value={inputsData.companyName}
            onChange={handleChangeInputs}
            onFocus={handleFocusToRemoveError}
            isError={!!loadingError}
            ref={companyNameInputRef}
            autoComplete='on'
          />
          <div className={styles.checkbox}>
            <Checkbox
              label={''}
              boxClassName={styles.checkbox_box}
              required
              checked={isPrivacyChecked}
              onChange={handleChangePrivacyChecked}
            />
            <span>
              {t('sign-up.privacy.prefix')}
              <Link to='/consent' target='_blank' className={styles.checkbox_link}>
                {t('sign-up.privacy.link')}
              </Link>{' '}
              {t('sign-up.privacy.suffix')}
            </span>
          </div>
        </>
      );
    }

    if (stepNum === 2) {
      return (
        <>
          <PhoneInputWithCountrySelect
            className={styles.input}
            defaultCountry='RU'
            inputComponent={PhoneInput}
            required
            value={phoneNum}
            onChange={handleChangePhone}
            ref={phoneInputRef}
            disabled={isLoading}
            isError={loadingError}
            onFocus={handleFocusToRemoveError}
          />
          <Input
            placeholder={t('inputs.email') || ''}
            fieldClassName={styles.input}
            type='email'
            id='email'
            required
            value={inputsData.email}
            onChange={handleChangeInputs}
            onFocus={handleFocusToRemoveError}
            isError={!!loadingError}
            ref={emailInputRef}
            autoComplete='on'
          />
          <Input
            placeholder={t('inputs.city') || ''}
            fieldClassName={styles.input}
            type='text'
            id='city'
            minLength={2}
            maxLength={20}
            value={inputsData.city}
            onChange={handleChangeInputs}
            onFocus={handleFocusToRemoveError}
            isError={!!loadingError}
            ref={cityInputRef}
            autoComplete='on'
          />
        </>
      );
    }

    return (
      <>
        <SmsInput
          inputsData={codeInputsData}
          setInputsData={setCodeInputsData}
          handleRequest={handleAuth}
          handleRemoveError={handleRemoveError}
          isError={!!loadingError}
          input0Ref={input0Ref}
        />
        <Button
          text={isLoading ? t('sign-in.loading') : codeBtnState.text}
          kind={EButtonKinds.signIn}
          className={clsx(styles.btn, styles.btn_code)}
          type='submit'
          disabled={isLoading || !!codeBtnState.sec}
        />
        <Button
          text={t('sign-up.change-phone')}
          kind={EButtonKinds.textOnly}
          className={styles.changePhoneBtn}
          type='button'
          onClick={handleChangePhoneBtnClick}
        />
      </>
    );
  };

  useEffect(() => {
    if (firstNameInputRef.current) {
      firstNameInputRef.current.focus();
    }
  }, [firstNameInputRef]);

  useEffect(() => {
    if (stepNum !== 1 && !inputsData.firstName) {
      navigate('/sign-up/1');
    }
  }, []);

  useEffect(() => {
    if (stepNum === 1) {
      firstNameInputFocus();
    }

    if (stepNum === 2) {
      phoneInputFocus();
      setIsHidden(false);
    }

    if (stepNum === 3) {
      setIsHidden(true);
    }
  }, [stepNum]);

  useEffect(
    () => () => {
      intervalId && clearInterval(intervalId);
    },
    [intervalId],
  );

  return (
    <form className={clsx(styles.form, className)} onSubmit={handleSubmit} ref={formRef} {...rest}>
      {stepNum === 3 && <Paragraph className={styles.text}>{paragraphText}</Paragraph>}
      <Paragraph className={clsx(styles.error, { [styles.error_active]: loadingError })} isError>
        {loadingError}
      </Paragraph>
      {renderStepContent()}
      {stepNum !== 3 && (
        <Button
          text={isLoading ? t('sign-in.loading') : t(`sign-up.submit-btn-${stepNum}`)}
          kind={EButtonKinds.signIn}
          className={styles.btn}
          type='submit'
          disabled={!phoneValidate[stepNum] || isLoading}
        />
      )}
      {stepNum !== 3 && (
        <>
          <ProgressBar
            step={stepNum}
            className={styles.progressBar}
            handleProgressBarFirstStep={handleProgressBarFirstStep}
            handleProgressBarSecondStep={handleProgressBarSecondStep}
          />
          <div className={styles.not_sign_up}>
            <Paragraph className={styles.not_sign_up_text}>{t('sign-up.have-account')}</Paragraph>
            <Link className={styles.not_sign_up_link} to='/sign-in'>
              {t('sign-up.have-account-link')}
            </Link>
          </div>
        </>
      )}
    </form>
  );
};

export default SignUpForm;
